import { StateSetter, useState } from 'react';
import { useLang } from 'hooks/lang';

import { actions, FullUserSuggestion, getStatusConfig, SuggestionComment } from 'entities';

import { Button, Column, Dot, DropdownMenu, DropdownMenuItem, Row } from 'components/Blocks';
import { Comments } from 'components/Comments';
import { WorkflowIcon } from 'components/Icons';

import { useUser } from 'hooks';

import { getFirst, sortByProperty } from 'utils/array';
import { cn } from 'styles';

interface SuggestionCommentsProps {
  suggestion: FullUserSuggestion;
  updateSuggestion: StateSetter<FullUserSuggestion>;
}

export function SuggestionComments({ suggestion, updateSuggestion }: SuggestionCommentsProps) {
  const user = useUser();
  const { t } = useLang();

  const currentConfig = getStatusConfig('userSuggestion', suggestion.status);

  const mustChangeStatusOnComment = currentConfig.adminCommentMustChangeStatus && user.isSysAdmin;

  const [nextStatus, setStatus] = useState(
    mustChangeStatusOnComment ? getFirst(currentConfig.statusTargets) : suggestion.status,
  );

  const nextConfig = getStatusConfig('userSuggestion', nextStatus);

  const create = actions.feedback.createSuggestionComment.use({
    onSuccess: (comment) => {
      updateSuggestion((old) => ({
        ...old,

        comments: [comment, ...(old.comments || [])],

        status: nextStatus,
      }));
    },
  });

  const createComment = async (newComment: string, files?: File[]): Promise<boolean | void> => {
    if (!newComment) return;

    const comment = await create.execute({
      suggestionId: suggestion.id,
      text: newComment,
      files,

      status: nextStatus === suggestion.status || !user.isSysAdmin ? undefined : nextStatus,
    });

    return !!comment;
  };

  const update = actions.feedback.updateSuggestionComment.use({
    onSuccess: (editedComment) => {
      updateSuggestion((old) => ({
        ...old,

        comments: old.comments.map((comment) =>
          comment.id === editedComment.id ? { ...editedComment, edited: true } : comment,
        ),
      }));
    },
  });

  const updateComment = async (text: string, editedComment: SuggestionComment): Promise<void> => {
    await update.execute({
      suggestionId: editedComment.suggestionId,
      id: editedComment.id,
      text,
    });
  };

  return (
    <Column gap="2" className="mt-2 pb-4">
      {user.isSysAdmin && !!currentConfig.statusTargets.length && currentConfig.canComment && (
        <>
          <hr />

          <Row gap="2">
            <WorkflowIcon size={14} />
            <span className="text-xs">{t('status-after-comment')}</span>

            <DropdownMenu
              align="end"
              trigger={
                <Button variant="ghost" size="small" className="ml-auto text-xs" color="gray">
                  <Dot color={nextConfig.color} size={8} />
                  <span style={{ color: nextConfig.color }}>{nextConfig.getLabel(t)}</span>
                </Button>
              }
            >
              {currentConfig.statusTargets.map((statusCode) => (
                <DropdownMenuItem key={statusCode} onClick={() => setStatus(statusCode)}>
                  <Dot color={getStatusConfig('userSuggestion', statusCode).color} size={8} />

                  <span style={{ color: getStatusConfig('userSuggestion', statusCode).color }}>
                    {getStatusConfig('userSuggestion', statusCode).getLabel(t)}
                  </span>
                </DropdownMenuItem>
              ))}
            </DropdownMenu>
          </Row>
        </>
      )}

      <Comments
        canEdit={currentConfig.canComment}
        comments={sortByProperty(suggestion.comments, 'timestamp', { timestamp: 'DESC' })}
        onCreate={createComment}
        onEdit={updateComment}
        className={cn('border-0 p-0', !currentConfig.canComment && 'mt-2')}
        historyTarget="feedback.suggestionCommentHistory"
        getExtraContent={(comment) =>
          !!comment.newStatus && (
            <span className="ml-1 text-xs italic">
              {t('status-changed-to')}{' '}
              <span
                style={{ color: getStatusConfig('userSuggestion', comment.newStatus).color }}
                className="font-semibold"
              >
                {getStatusConfig('userSuggestion', comment.newStatus).getLabel(t)}
              </span>
            </span>
          )
        }
      />
    </Column>
  );
}
